import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Send, KeyRound, Loader2, Lightbulb, ListOrdered, PencilRuler } from "lucide-react";
import { loadLocalKey, saveLocalKey } from "@/lib/idb";

const MODE_BTNS = [
  { mode: "explain_simply", label: "Explain simply", icon: Lightbulb },
  { mode: "step_hint", label: "Step-by-step hint", icon: ListOrdered },
  { mode: "practice_problem", label: "Practice problem", icon: PencilRuler },
];

export function AgentDock({ getSelection }) {
  const [key, setKey] = useState(loadLocalKey());
  const [ctx, setCtx] = useState({ board_code: null, grade: null });
  const [editingKey, setEditingKey] = useState(false);
  const [draftKey, setDraftKey] = useState("");
  const [q, setQ] = useState("");
  const [messages, setMessages] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/open-school/settings").then(r => {
      const k = r.data.gemini_key || loadLocalKey();
      if (k) { setKey(k); saveLocalKey(k); }
      setCtx({ board_code: r.data.board_code, grade: r.data.grade });
    }).catch(() => { const k = loadLocalKey(); if (k) setKey(k); });
  }, []);

  const saveKey = async () => {
    const k = draftKey.trim();
    saveLocalKey(k); setKey(k); setEditingKey(false);
    try { await api.put("/open-school/settings", { gemini_key: k }); toast.success("Gemini key saved"); }
    catch { toast.error("Saved locally; cloud sync failed"); }
  };

  const ask = async (mode = "ask") => {
    if (!key) { setEditingKey(true); toast.info("Add your Gemini API key first"); return; }
    const selection = getSelection ? (getSelection() || "") : "";
    if (mode === "ask" && !q.trim() && !selection) { toast.info("Type a question or highlight some notes"); return; }
    const label = mode === "ask" ? (q.trim() || "(highlighted text)")
      : `${MODE_BTNS.find(m => m.mode === mode)?.label}${selection ? ": " + selection.slice(0, 40) + "…" : ""}`;
    setMessages(m => [...m, { role: "user", text: label }]);
    setBusy(true);
    try {
      const r = await api.post("/ponderr-agent/tutor", {
        query: q.trim(), gemini_key: key, mode, selection_text: selection,
        board_code: ctx.board_code, grade: ctx.grade,
      });
      setMessages(m => [...m, { role: "agent", text: r.data.text, sources: r.data.sources || [] }]);
      setQ("");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Tutor request failed";
      setMessages(m => [...m, { role: "agent", text: `⚠️ ${msg}`, error: true }]);
      toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <div className="flex flex-col h-full bg-[#0B1120] text-white" data-testid="agent-dock">
      <div className="p-3 border-b-2 border-black bg-[#C4B5FD] text-black flex items-center gap-2">
        <Sparkles size={18} />
        <div className="font-black display text-sm">Ponderr Agent · Socratic Tutor</div>
        <button className="ml-auto nb-btn nb-btn-ghost !text-black px-2 py-1 text-[11px]"
          onClick={() => { setDraftKey(key); setEditingKey(v => !v); }} data-testid="agent-key-toggle">
          <KeyRound size={13} /> {key ? "Key ✓" : "Add Key"}
        </button>
      </div>

      {editingKey && (
        <div className="p-3 bg-[#1E293B] space-y-2 border-b border-white/10" data-testid="agent-key-editor">
          <div className="text-[11px] opacity-80">Your Personal Google Gemini API Key (free from Google AI Studio). Stored on this device + synced to your profile.</div>
          <input type="password" className="nb-input !text-black w-full text-sm" placeholder="AIza…"
            value={draftKey} onChange={e => setDraftKey(e.target.value)} data-testid="agent-key-input" />
          <div className="flex gap-2">
            <button className="nb-btn text-xs" onClick={saveKey} data-testid="agent-key-save">Save Key</button>
            <a className="nb-btn nb-btn-ghost !text-white text-xs" href="https://aistudio.google.com/apikey" target="_blank" rel="noreferrer">Get a free key →</a>
          </div>
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-3 space-y-3 text-sm" data-testid="agent-messages">
        {!messages.length && (
          <div className="opacity-60 text-xs leading-relaxed">
            Highlight any note text, then tap <b>Explain simply</b>, <b>Step-by-step hint</b> or <b>Practice problem</b> — or just ask a question. I'll guide you Socratically, never just hand over the answer.
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`rounded-xl px-3 py-2 ${m.role === "user" ? "bg-[#334155] ml-6" : m.error ? "bg-[#7f1d1d] mr-6" : "bg-[#166534] mr-6"}`}>
            <div className="whitespace-pre-wrap">{m.text}</div>
            {m.sources?.length > 0 && (
              <div className="mt-1 text-[10px] opacity-70">📚 {m.sources.length} curriculum snippet(s)</div>
            )}
          </div>
        ))}
        {busy && <div className="flex items-center gap-2 text-xs opacity-70"><Loader2 size={14} className="animate-spin" /> Thinking…</div>}
      </div>

      <div className="p-3 border-t-2 border-black bg-[#1E293B] space-y-2">
        <div className="flex gap-1 flex-wrap">
          {MODE_BTNS.map(b => (
            <button key={b.mode} className="nb-btn nb-btn-ghost !text-white text-[11px] px-2 py-1"
              disabled={busy} onClick={() => ask(b.mode)} data-testid={`agent-mode-${b.mode}`}>
              <b.icon size={12} /> {b.label}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <input className="nb-input !text-black flex-1 text-sm" placeholder="Ask a question…"
            value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === "Enter" && ask("ask")}
            data-testid="agent-question" />
          <button className="nb-btn" disabled={busy} onClick={() => ask("ask")} data-testid="agent-send"><Send size={15} /></button>
        </div>
      </div>
    </div>
  );
}
